/**
 * How close a satellite is to the CONCAT_WS ceiling on a T-SQL target.
 *
 * A satellite's change-detection hash is one `CONCAT_WS` over every non-key column, and SQL Server
 * accepts at most 253 values in one. Measured against a real server rather than taken from
 * documentation: 253 succeeds, 254 raises.
 *
 * The server rejects an over-wide statement at EXECUTION, so without a warning here the first
 * sign is a failed pipeline run at the customer. The backend now refuses to generate such an
 * entity (`require_hashable_column_count`); this exists so nobody reaches that point unaware.
 *
 * Spark's `concat_ws` takes a variadic array with no equivalent cap, so lakehouse targets are
 * exempt. Fabric warehouse is treated as limited: its ceiling was not measured, but it is T-SQL
 * emitting the same function, and the cost of being wrong is asymmetric.
 */

export const CONCAT_WS_MAX_VALUES = 253;

/** Show the budget from here, so a wide satellite is visible well before it is a problem. */
export const CONCAT_WS_WARN_FROM = 200;

const LIMITED_TARGETS = new Set([
  "SQLPOOL_DEDICATED",
  "SQL_SERVER_2019",
  "AZURE_SQL_DB",
  "FABRIC_DW"
]);

export const isConcatWsLimitedTarget = (value: string | null | undefined): boolean =>
  LIMITED_TARGETS.has((value ?? "").toString().trim().toUpperCase());

export type HashColumnBudget = {
  used: number;
  limit: number;
  /** Past the ceiling — the backend will refuse to generate this entity. */
  exceeded: boolean;
  message: string;
};

/**
 * Returns null when there is nothing worth saying — a target without the limit, or a satellite
 * comfortably below the threshold. A warning that is always on stops being read.
 */
export const getHashColumnBudget = (
  targetType: string | null | undefined,
  hashedColumnCount: number
): HashColumnBudget | null => {
  if (!isConcatWsLimitedTarget(targetType)) {
    return null;
  }
  if (hashedColumnCount < CONCAT_WS_WARN_FROM) {
    return null;
  }
  const exceeded = hashedColumnCount > CONCAT_WS_MAX_VALUES;
  return {
    used: hashedColumnCount,
    limit: CONCAT_WS_MAX_VALUES,
    exceeded,
    message: exceeded
      ? `${hashedColumnCount} of ${CONCAT_WS_MAX_VALUES} change-detection columns — over the limit ` +
        `for this target, so this satellite will not generate. Remove at least ` +
        `${hashedColumnCount - CONCAT_WS_MAX_VALUES}, or split it into two satellites over the same hub.`
      : `${hashedColumnCount} of ${CONCAT_WS_MAX_VALUES} change-detection columns used.`
  };
};
